import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNotification } from "../../../../components/Notification/NotificationManager";

import { getAdherentsByUser } from "../../../../services/adherentServices";

import {
  getPrepaExercisesByAdherent,
  updateAdherentExerciseResult,
} from "../../../../services/prepaPhysiqueServices";

import {
  buildAdherentPrepaStats,
  buildExerciseKey,
  getSelectedAdherent,
  normalizeAdherentExercisesFromApi,
  sortAdherentExercises,
  toBoolean,
} from "../utils/preparationPhysiqueAdherentUtils";

export function usePreparationPhysiqueAdherentPage() {
  const { addNotification } = useNotification();

  const notificationRef = useRef(addNotification);
  const hasLoadedOnceRef = useRef(false);

  const [adherents, setAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState(null);
  const [exercises, setExercises] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [validatingKeys, setValidatingKeys] = useState([]);

  useEffect(() => {
    notificationRef.current = addNotification;
  }, [addNotification]);

  const selectedAdherent = useMemo(() => {
    return getSelectedAdherent(adherents, selectedAdherentId);
  }, [adherents, selectedAdherentId]);

  const stats = useMemo(() => {
    return buildAdherentPrepaStats(exercises);
  }, [exercises]);

  const fetchAdherents = useCallback(async () => {
    const response = await getAdherentsByUser();
    const safeAdherents = Array.isArray(response) ? response : [];

    setAdherents(safeAdherents);

    if (safeAdherents.length > 0) {
      setSelectedAdherentId((currentId) => currentId || safeAdherents[0].id);
    }

    return safeAdherents;
  }, []);

  const fetchExercisesForAdherent = useCallback(async (adherentId) => {
    if (!adherentId) {
      setExercises([]);
      return;
    }

    const response = await getPrepaExercisesByAdherent(adherentId);

    const normalizedExercises = normalizeAdherentExercisesFromApi(response);

    setExercises(sortAdherentExercises(normalizedExercises));
  }, []);

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      const isInitialLoad = !hasLoadedOnceRef.current;

      if (isInitialLoad) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      try {
        let currentAdherents = adherents;

        if (!currentAdherents.length) {
          currentAdherents = await fetchAdherents();
        }

        if (!currentAdherents.length) {
          setExercises([]);

          notificationRef.current?.(
            "Aucun adhérent n'est rattaché à votre compte.",
            "error",
          );

          return;
        }

        const adherentId = selectedAdherentId || currentAdherents[0]?.id;

        if (!adherentId) {
          notificationRef.current?.(
            "Impossible d'identifier l'adhérent sélectionné.",
            "error",
          );

          return;
        }

        await fetchExercisesForAdherent(adherentId);

        if (!silent && !isInitialLoad) {
          notificationRef.current?.(
            "Préparation physique actualisée.",
            "success",
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération de la préparation physique :",
          error,
        );

        notificationRef.current?.(
          "Erreur lors de la récupération des exercices.",
          "error",
        );
      } finally {
        hasLoadedOnceRef.current = true;
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      adherents,
      fetchAdherents,
      fetchExercisesForAdherent,
      selectedAdherentId,
    ],
  );

  useEffect(() => {
    fetchAdherents().catch((error) => {
      console.error("Erreur lors de la récupération des adhérents :", error);

      notificationRef.current?.(
        "Erreur lors de la récupération des adhérents.",
        "error",
      );

      setLoading(false);
    });
  }, [fetchAdherents]);

  useEffect(() => {
    if (!selectedAdherentId) return;

    fetchData({ silent: true });
  }, [fetchData, selectedAdherentId]);

  const handleSelectAdherent = useCallback((adherentId) => {
    setSelectedAdherentId(adherentId);
  }, []);

  const handleValidateExercise = useCallback(
    async (exercise) => {
      if (!selectedAdherentId) {
        notificationRef.current?.("Aucun adhérent sélectionné.", "error");
        return;
      }

      if (!exercise?.id) {
        notificationRef.current?.("Exercice non identifié.", "error");
        return;
      }

      if (toBoolean(exercise.is_done)) {
        notificationRef.current?.("Cet exercice est déjà validé.", "error");
        return;
      }

      const confirmValidate = window.confirm(
        `Valider l'exercice "${exercise.nom}" comme réalisé ?`,
      );

      if (!confirmValidate) return;

      const exerciseKey = buildExerciseKey(exercise);

      setValidatingKeys((prev) => [...prev, exerciseKey]);

      try {
        await updateAdherentExerciseResult({
          adherent_id: selectedAdherentId,
          prepa_physique_id: exercise.id,
          date_realisation: exercise.date_realisation,
          repetitions: exercise.repetitions,
          is_done: true,
        });

        notificationRef.current?.(
          "Exercice validé avec succès.",
          "success",
        );

        await fetchExercisesForAdherent(selectedAdherentId);
      } catch (error) {
        console.error("Erreur lors de la validation de l'exercice :", error);

        notificationRef.current?.(
          error?.response?.data?.message ||
            "Erreur lors de la validation de l'exercice.",
          "error",
        );
      } finally {
        setValidatingKeys((prev) =>
          prev.filter((key) => key !== exerciseKey),
        );
      }
    },
    [fetchExercisesForAdherent, selectedAdherentId],
  );

  return {
    adherents,
    selectedAdherentId,
    selectedAdherent,
    exercises,
    loading,
    refreshing,
    validatingKeys,
    stats,
    fetchData,
    handleSelectAdherent,
    handleValidateExercise,
  };
}